import { call, put, takeEvery } from 'redux-saga/effects';
import {
  CASE_GENERATE_REFERENCE_CANDIDATES_BEGIN,
  CASE_GENERATE_REFERENCE_CANDIDATES_SUCCESS,
  CASE_GENERATE_REFERENCE_CANDIDATES_FAILURE,
  CASE_GENERATE_REFERENCE_CANDIDATES_DISMISS_FEEDBACK,
} from './constants';
import {
  createPromiseAction,
  resolvePromiseAction,
  rejectPromiseAction,
} from '@adobe/redux-saga-promise';
import api from 'common/api';
import { withCurrentCaseId } from 'common/selectors';

export const generateReferenceCandidates = createPromiseAction(
  CASE_GENERATE_REFERENCE_CANDIDATES_BEGIN,
  (fileId, options = {}) => ({ fileId, ...options }),
);

export function dismissGenerateReferenceCandidatesFeedback() {
  return {
    type: CASE_GENERATE_REFERENCE_CANDIDATES_DISMISS_FEEDBACK,
  };
}

export function* doGenerateReferenceCandidates(action) {
  const {
    payload: { fileId, caseId },
  } = action;

  const res = yield call(api.post, `/cases/${caseId}/files/${fileId}/exhibit-process?preview=true`);

  if (res && res.error) {
    yield put({
      type: CASE_GENERATE_REFERENCE_CANDIDATES_FAILURE,
      feedback: {
        message: 'feedback.generateReferenceCandidatesFailure',
        params: { fileId },
        error: res.error,
        retryAction: action,
      },
    });
    return yield call(rejectPromiseAction, action, res.error);
  }

  const resolvedUpdatedReferences = Array.isArray(res?.updatedReferences)
    ? res.updatedReferences
    : Array.isArray(res?.candidates)
      ? res.candidates
      : Array.isArray(res)
        ? res
        : [];
  const resolvedFileDocumentUpdates = Array.isArray(res?.fileDocumentUpdates)
    ? res.fileDocumentUpdates
    : [];
  const resolvedCollisionReassignments = Array.isArray(res?.collisionReassignments)
    ? res.collisionReassignments
    : [];
  const resolvedUnresolvedReferences = Array.isArray(res?.unresolvedReferences)
    ? res.unresolvedReferences
    : [];

  const resolvedReferencesFound = Number.isFinite(Number(res?.referencesFound))
    ? Number(res?.referencesFound)
    : resolvedUpdatedReferences.length;
  const resolvedReferencesUpdated = Number.isFinite(Number(res?.referencesUpdated))
    ? Number(res?.referencesUpdated)
    : resolvedUpdatedReferences.length;

  const data = Array.isArray(res)
    ? {
        fileId,
        updatedReferences: resolvedUpdatedReferences,
        fileDocumentUpdates: resolvedFileDocumentUpdates,
        collisionReassignments: resolvedCollisionReassignments,
        unresolvedReferences: resolvedUnresolvedReferences,
        referencesFound: resolvedReferencesFound,
        referencesUpdated: resolvedReferencesUpdated,
        candidates: resolvedUpdatedReferences,
      }
    : {
        fileId,
        ...(res || {}),
        updatedReferences: resolvedUpdatedReferences,
        fileDocumentUpdates: resolvedFileDocumentUpdates,
        collisionReassignments: resolvedCollisionReassignments,
        unresolvedReferences: resolvedUnresolvedReferences,
        referencesFound: resolvedReferencesFound,
        referencesUpdated: resolvedReferencesUpdated,
        candidates: resolvedUpdatedReferences,
      };

  yield put({
    type: CASE_GENERATE_REFERENCE_CANDIDATES_SUCCESS,
    data,
    feedback: {
      message: 'feedback.generateReferenceCandidatesSuccess',
      params: { fileId },
      success: true,
    },
  });

  return yield call(resolvePromiseAction, action, data);
}

export function* watchGenerateReferenceCandidates() {
  yield takeEvery(generateReferenceCandidates, withCurrentCaseId(doGenerateReferenceCandidates));
}

export function reducer(state, action) {
  switch (action.type) {
    case CASE_GENERATE_REFERENCE_CANDIDATES_BEGIN + '.TRIGGER':
      return {
        ...state,
        generateReferenceCandidatesPending: true,
        generateReferenceCandidatesFeedback: null,
        generateReferenceCandidatesResult: null,
      };

    case CASE_GENERATE_REFERENCE_CANDIDATES_SUCCESS:
      return {
        ...state,
        generateReferenceCandidatesPending: false,
        generateReferenceCandidatesFeedback: action.feedback,
        generateReferenceCandidatesResult: action.data,
      };

    case CASE_GENERATE_REFERENCE_CANDIDATES_FAILURE:
      return {
        ...state,
        generateReferenceCandidatesPending: false,
        generateReferenceCandidatesFeedback: action.feedback,
        generateReferenceCandidatesResult: null,
      };

    case CASE_GENERATE_REFERENCE_CANDIDATES_DISMISS_FEEDBACK:
      return {
        ...state,
        generateReferenceCandidatesFeedback: null,
        generateReferenceCandidatesResult: null,
      };

    default:
      return state;
  }
}
